import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Brain, PenTool, Shuffle, Volume2, Star, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';

const Games = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;

  const allGames = [
    {
      id: 'flashcards',
      title: 'Flashcards',
      description: 'Practice vocabulary with interactive flashcards',
      icon: BookOpen,
      difficulty: 'Beginner',
      estimatedTime: '5-10 min',
      color: 'from-green-500 to-green-600',
      path: '/games/flashcards'
    },
    {
      id: 'multiple-choice',
      title: 'Multiple Choice Quiz',
      description: 'Test your knowledge with multiple choice questions',
      icon: Brain,
      difficulty: 'Intermediate',
      estimatedTime: '10-15 min',
      color: 'from-red-500 to-red-600',
      path: '/games/multiple-choice'
    },
    {
      id: 'fill-blank',
      title: 'Fill in the Blanks',
      description: 'Complete sentences with the correct Algerian words',
      icon: PenTool,
      difficulty: 'Intermediate',
      estimatedTime: '8-12 min',
      color: 'from-green-600 to-red-600',
      path: '/games/fill-blank'
    },
    {
      id: 'memory-match',
      title: 'Memory Match',
      description: 'Match Algerian words with their English translations',
      icon: Shuffle,
      difficulty: 'Beginner',
      estimatedTime: '5-8 min',
      color: 'from-red-600 to-green-600',
      path: '/games/memory-match'
    },
    {
      id: 'pronunciation',
      title: 'Pronunciation Practice',
      description: 'Listen and practice pronunciation of Algerian words',
      icon: Volume2,
      difficulty: 'All Levels',
      estimatedTime: '10-15 min',
      color: 'from-green-500 to-red-500',
      path: '/games/pronunciation'
    },
    {
      id: 'possessives',
      title: 'Possessives Game',
      description: 'Learn to express ownership - my, your, his, her, etc.',
      icon: Users,
      difficulty: 'Intermediate',
      estimatedTime: '8-12 min',
      color: 'from-purple-500 to-pink-500',
      path: '/games/possessives',
      isNew: true
    }
  ];

  // Filter games - show adminOnly games only to admins
  const games = allGames.filter(game => !game.adminOnly || isAdmin);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">
          Learning Games
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Master Algerian dialect through interactive games and challenges. 
          Choose your learning style and start practicing!
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {games.map((game) => {
          const IconComponent = game.icon;
          return (
            <Link key={game.id} to={game.path}>
              <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer h-full border-0 shadow-md hover:scale-105 relative">
                {game.isNew && (
                  <div className="absolute -top-2 -right-2 z-10">
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white animate-pulse">
                      NEW
                    </Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${game.color} rounded-full mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-900 group-hover:text-green-700 transition-colors font-heading">
                    {game.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {game.description}
                  </p>
                  
                  <div className="flex justify-between items-center mb-3">
                    <Badge className={getDifficultyColor(game.difficulty)}>
                      {game.difficulty}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {game.estimatedTime}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-sm text-green-600 font-medium group-hover:text-green-700">
                    <Star className="w-4 h-4 mr-1" />
                    Start Playing
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div className="mt-12 bg-white/50 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          Your Learning Journey
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">5</div>
            <div className="text-gray-600">Games Available</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600 mb-2">18</div>
            <div className="text-gray-600">Vocabulary Words</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-700 mb-2">100%</div>
            <div className="text-gray-600">Interactive Learning</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Games;